import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './AdminDashboard.css';
import './AnalyticsDashboard.css';

const AnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [popularVehicles, setPopularVehicles] = useState([]);
  const [popularSearches, setPopularSearches] = useState([]);
  const [loading, setLoading] = useState(true);

  const API_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:3000/api'}/analytics`;

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/login');
  };

  const tabs = [
    { id: 'vehicles', label: 'Vehículos', icon: '🚗', path: '/admin' },
    { id: 'bookings', label: 'Reservas', icon: '📋', path: '/admin' },
    { id: 'locations', label: 'Ubicaciones', icon: '📍', path: '/admin' },
    { id: 'additionals', label: 'Adicionales', icon: '📦', path: '/admin' },
    { id: 'analytics', label: 'Analytics', icon: '📊', path: '/admin/analytics' }
  ];

  const loadAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Loading analytics from:', API_URL);
      
      const [statsRes, funnelRes, vehiclesRes, searchesRes] = await Promise.all([
        axios.get(`${API_URL}/stats`),
        axios.get(`${API_URL}/funnel`),
        axios.get(`${API_URL}/popular-vehicles`),
        axios.get(`${API_URL}/popular-searches`)
      ]);

      console.log('Analytics loaded:', { statsRes, funnelRes, vehiclesRes, searchesRes });

      setStats(statsRes.data);
      setFunnel(funnelRes.data);
      setPopularVehicles(vehiclesRes.data || []);
      setPopularSearches(searchesRes.data || []);
    } catch (error) {
      console.error('Error loading analytics:', error);
      console.error('Error details:', error.response?.data || error.message);
      // Establecer valores por defecto en caso de error
      setStats({
        uniqueUsers: 0,
        uniqueSessions: 0,
        searchesPerformed: 0,
        bookingAttempts: 0,
        conversionRate: 0
      });
      setFunnel({
        pageViews: 0,
        searches: 0,
        vehicleViews: 0,
        consultClicks: 0,
        bookingAttempts: 0
      });
      setPopularVehicles([]);
      setPopularSearches([]);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  if (loading) {
    return <div className="dashboard-loading">Cargando analytics...</div>;
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>Panel de Administración</h1>
        <div className="header-actions">
          <button className="logout-btn" onClick={handleLogout}>
            🚪 Cerrar Sesión
          </button>
          <button className="back-btn" onClick={() => navigate('/')}>
            ← Volver al sitio
          </button>
        </div>
      </div>

      <div className="admin-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-btn ${tab.id === 'analytics' ? 'active' : ''}`}
            onClick={() => navigate(tab.path)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="admin-content">
        <div className="analytics-dashboard">
          <div className="dashboard-container">
            <h2>Dashboard de Analytics - Fake Door</h2>

        <div className="stats-grid">
          <div className="stat-card">
            <h3>Usuarios Únicos</h3>
            <div className="stat-value">{stats?.uniqueUsers || 0}</div>
          </div>
          <div className="stat-card">
            <h3>Sesiones</h3>
            <div className="stat-value">{stats?.uniqueSessions || 0}</div>
          </div>
          <div className="stat-card">
            <h3>Búsquedas</h3>
            <div className="stat-value">{stats?.searchesPerformed || 0}</div>
          </div>
          <div className="stat-card highlight">
            <h3>Intentos de Reserva</h3>
            <div className="stat-value">{stats?.bookingAttempts || 0}</div>
          </div>
          <div className="stat-card highlight">
            <h3>Tasa de Conversión</h3>
            <div className="stat-value">{stats?.conversionRate || 0}%</div>
          </div>
        </div>

        <div className="dashboard-row">
          <div className="dashboard-card">
            <h2>Funnel de Conversión</h2>
            <div className="funnel">
              <div className="funnel-step">
                <span className="funnel-label">Visitas</span>
                <div className="funnel-bar" style={{ width: '100%' }}>
                  <span>{funnel?.pageViews || 0}</span>
                </div>
              </div>
              <div className="funnel-step">
                <span className="funnel-label">Búsquedas</span>
                <div 
                  className="funnel-bar" 
                  style={{ 
                    width: `${(funnel?.searches / funnel?.pageViews * 100) || 0}%` 
                  }}
                >
                  <span>{funnel?.searches || 0}</span>
                </div>
              </div>
              <div className="funnel-step">
                <span className="funnel-label">Vehículos Vistos</span>
                <div 
                  className="funnel-bar" 
                  style={{ 
                    width: `${(funnel?.vehicleViews / funnel?.pageViews * 100) || 0}%` 
                  }}
                >
                  <span>{funnel?.vehicleViews || 0}</span>
                </div>
              </div>
              <div className="funnel-step">
                <span className="funnel-label">Clicks en Consultar</span>
                <div 
                  className="funnel-bar" 
                  style={{ 
                    width: `${(funnel?.consultClicks / funnel?.pageViews * 100) || 0}%` 
                  }}
                >
                  <span>{funnel?.consultClicks || 0}</span>
                </div>
              </div>
              <div className="funnel-step">
                <span className="funnel-label">Intentos de Reserva</span>
                <div 
                  className="funnel-bar highlight" 
                  style={{ 
                    width: `${(funnel?.bookingAttempts / funnel?.pageViews * 100) || 0}%` 
                  }}
                >
                  <span>{funnel?.bookingAttempts || 0}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-row">
          <div className="dashboard-card">
            <h2>Vehículos Más Consultados</h2>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Vehículo</th>
                  <th>Precio</th>
                  <th>Consultas</th>
                </tr>
              </thead>
              <tbody>
                {popularVehicles.length > 0 ? (
                  popularVehicles.map((vehicle, index) => (
                    <tr key={index}>
                      <td>{vehicle._id.vehicleName}</td>
                      <td>$ {vehicle._id.price?.toLocaleString('es-AR')}</td>
                      <td><strong>{vehicle.count}</strong></td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" style={{ textAlign: 'center', padding: '20px' }}>
                      No hay datos disponibles
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="dashboard-card">
            <h2>Búsquedas Más Populares</h2>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Ubicación</th>
                  <th>Duración</th>
                  <th>Búsquedas</th>
                </tr>
              </thead>
              <tbody>
                {popularSearches.length > 0 ? (
                  popularSearches.map((search, index) => (
                    <tr key={index}>
                      <td>{search._id.pickupLocation}</td>
                      <td>{search._id.duration} días</td>
                      <td><strong>{search.count}</strong></td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" style={{ textAlign: 'center', padding: '20px' }}>
                      No hay datos disponibles
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="dashboard-actions">
          <button onClick={loadAnalytics} className="refresh-button">
            🔄 Actualizar Datos
          </button>
        </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
